import React from "react";
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BusinessIcon from "@mui/icons-material/Business";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import CloseIcon from "@mui/icons-material/Close";
import { FeedType, VideoSource } from "../../Enums/enums";
import {
  isCategoryMissing,
  SOCIAL_FEED_PLATFORMS,
  PARTNER_FEED_PLATFORMS,
} from "../../Enums/platform_constants";
import PlatformSourceCard from "../components/PlatformSourceCard";
import useBrandTemplates from "../hooks/useBrandTemplates";
import BatchEditToolbar from "../batchEditor/BatchEditToolbar";
import { hasReviewedEdit } from "../batchEditor/batchItems";
import { feedLabel, feedPartnerPlatforms, selectedPartnerFeeds } from "../partnerFeeds";

/** Returns the platforms available for a brand based on its selected feed types. */
function getAvailablePlatforms(brandName, selectedFeeds, brandFeedsInfo) {
  const feedIds = selectedFeeds[brandName] || [];
  const feeds = brandFeedsInfo[brandName] || [];
  const selected = feeds.filter((f) => feedIds.includes(f.id));
  const hasSocial = selected.some((f) => f.feed_type === FeedType.SOCIAL);
  const hasCustom = selected.some((f) => f.feed_type === FeedType.PARTNER);
  const platforms = [];
  if (hasSocial) platforms.push(...SOCIAL_FEED_PLATFORMS);
  if (hasCustom) platforms.push(...PARTNER_FEED_PLATFORMS);
  // Fallback: if feeds not loaded yet, show all
  if (platforms.length === 0 && feedIds.length === 0) {
    return [...SOCIAL_FEED_PLATFORMS, ...PARTNER_FEED_PLATFORMS];
  }
  return platforms;
}

/** Same resolution rule used everywhere in this step (unchanged logic). */
function isComboResolved(video, platform, cfg) {
  if (!cfg) return false;
  const src = cfg.source;
  let resolved = false;
  if (src === VideoSource.ORIGINAL) resolved = true;
  else if (src === VideoSource.LOCAL) resolved = video.is_compilation || !!cfg.localVideoPath;
  else if (src === VideoSource.EDITED) resolved = hasReviewedEdit(cfg);
  else if (src === VideoSource.DUPLICATE) resolved = !!cfg.duplicateOf;
  if (!resolved) return false;
  if (isCategoryMissing(platform, cfg)) return false;
  return true;
}

const focusRing = {
  "&:focus-within": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
};

function PlatformCheckboxes({ title, titleColor, platforms, selectedPlatforms, onToggle }) {
  return (
    <Box mb={1}>
      <Typography variant="caption" color={titleColor} fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
        {title}
      </Typography>
      <FormGroup
        sx={{
          display: "grid",
          gap: 0.5,
          mt: 0.5,
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
        }}
      >
        {platforms.map((p) => (
          <FormControlLabel
            key={p}
            sx={{ m: 0, minHeight: 44, pr: 1, border: "1px solid", borderColor: selectedPlatforms.includes(p) ? "primary.main" : "divider", borderRadius: 1.5, ...focusRing }}
            control={<Checkbox checked={selectedPlatforms.includes(p)} onChange={() => onToggle(p)} />}
            label={<Typography variant="body2">{p}</Typography>}
          />
        ))}
      </FormGroup>
    </Box>
  );
}

function VideoSection({ index, video, selectedPlatforms, videoConfigs, getTemplate, getTemplateChoices, onConfigChange }) {
  const unresolvedCount = selectedPlatforms.filter(
    (p) => !isComboResolved(video, p, videoConfigs[p])
  ).length;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, mb: 1.5, borderRadius: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} mb={1}>
        <Stack direction="row" alignItems="center" spacing={1} minWidth={0} sx={{ maxWidth: "100%" }}>
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{
              minWidth: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {index}
          </Typography>
          <VideoLibraryIcon fontSize="small" color="action" />
          <Box minWidth={0}>
            <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
              {video.title || video.video_title || video.unique_key}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({video.unique_key})
            </Typography>
          </Box>
        </Stack>
        {unresolvedCount === 0 ? (
          <Chip label="All configured" color="success" size="small" />
        ) : (
          <Chip label={`${unresolvedCount} pending`} color="warning" size="small" />
        )}
      </Stack>

      {selectedPlatforms.map((platform) => (
        <PlatformSourceCard
          key={platform}
          platform={platform}
          video={video}
          config={videoConfigs[platform] || {}}
          selectedPlatforms={selectedPlatforms}
          platformConfigs={videoConfigs}
          getTemplate={getTemplate}
          getTemplateChoices={getTemplateChoices}
          onChange={(p, partialConfig) => onConfigChange(p, partialConfig)}
        />
      ))}
    </Paper>
  );
}

/**
 * Partner platforms per Partner feed — shown only when the brand has two or
 * more Partner feeds (user request 2026-10-14); otherwise the single list
 * below is used, exactly as before. Ticking or unticking on one feed writes
 * every feed's list (so the others keep what they show now) and keeps the
 * brand's Partner selection as the union of them all.
 */
function PartnerFeedPlatforms({ brandName, feeds, platforms, selectedPlatforms, partnerMap, onPartnerFeedPlatformsChange, onPlatformsChange }) {
  const listFor = (feedId) =>
    feedPartnerPlatforms(brandName, feedId, { [brandName]: selectedPlatforms }, { [brandName]: partnerMap || {} });

  const toggle = (feedId, platform) => {
    const next = {};
    feeds.forEach((f) => {
      next[f.id] = listFor(f.id);
    });
    next[feedId] = next[feedId].includes(platform)
      ? next[feedId].filter((p) => p !== platform)
      : [...next[feedId], platform];
    const union = platforms.filter((p) => Object.values(next).some((list) => list.includes(p)));
    const nonPartner = selectedPlatforms.filter((p) => !PARTNER_FEED_PLATFORMS.includes(p));
    onPartnerFeedPlatformsChange(next);
    onPlatformsChange([...nonPartner, ...union]);
  };

  return (
    <Box mb={1} data-testid="partner-feed-platforms">
      <Typography variant="caption" color="warning.dark" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
        Custom / Distribution Platforms — per Partner feed
      </Typography>
      {feeds.map((feed) => {
        const list = listFor(feed.id);
        return (
          <Box key={feed.id} mt={0.75} pl={1} sx={{ borderLeft: "3px solid", borderColor: list.length ? "warning.light" : "error.main" }}>
            <PlatformCheckboxes
              title={feedLabel(feed)}
              titleColor="text.secondary"
              platforms={platforms}
              selectedPlatforms={list}
              onToggle={(p) => toggle(feed.id, p)}
            />
            {list.length === 0 && (
              <Typography variant="caption" color="error" display="block" mb={0.5}>
                Tick at least one platform for {feedLabel(feed)}, or deselect this feed in Step 1.
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function BrandSection({ brand, videos, selectedPlatforms, availablePlatforms, onPlatformsChange, brandConfigs, onConfigChange, partnerFeeds = [], partnerMap, onPartnerFeedPlatformsChange }) {
  const { getTemplate, getTemplateChoices } = useBrandTemplates(brand.name);

  const socialAvailable = availablePlatforms.filter((p) => SOCIAL_FEED_PLATFORMS.includes(p));
  const customAvailable = availablePlatforms.filter((p) => PARTNER_FEED_PLATFORMS.includes(p));

  const togglePlatform = (platform) => {
    const next = selectedPlatforms.includes(platform)
      ? selectedPlatforms.filter((p) => p !== platform)
      : [...selectedPlatforms, platform];
    onPlatformsChange(next);
  };

  const removePlatform = (platform) => {
    onPlatformsChange(selectedPlatforms.filter((p) => p !== platform));
  };

  const totalCombos = videos.length * selectedPlatforms.length;
  const resolvedCombos = videos.reduce(
    (sum, v) =>
      sum + selectedPlatforms.filter((p) => isComboResolved(v, p, brandConfigs[v.unique_key]?.[p])).length,
    0
  );

  return (
    <Accordion defaultExpanded disableGutters variant="outlined" sx={{ mb: 1.5, borderRadius: 2, "&:before": { display: "none" }, "@media (prefers-reduced-motion: reduce)": { "& .MuiCollapse-root": { transition: "none" } } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, px: { xs: 1.5, sm: 2 }, "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 } }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ pr: 1 }}>
          <BusinessIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>{brand.name}</Typography>
          {totalCombos > 0 && (
            <Chip
              label={`${resolvedCombos}/${totalCombos} configured`}
              size="small"
              color={resolvedCombos === totalCombos ? "success" : "warning"}
            />
          )}
          {selectedPlatforms.length === 0 && (
            <Chip label="No platforms selected" size="small" color="error" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        {/* Per-brand platform selection, grouped by feed type */}
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            Platforms for {brand.name}
          </Typography>

          {socialAvailable.length > 0 && (
            <PlatformCheckboxes title="Social Platforms" titleColor="primary" platforms={socialAvailable} selectedPlatforms={selectedPlatforms} onToggle={togglePlatform} />
          )}

          {customAvailable.length > 0 && partnerFeeds.length >= 2 && (
            <PartnerFeedPlatforms
              brandName={brand.name}
              feeds={partnerFeeds}
              platforms={customAvailable}
              selectedPlatforms={selectedPlatforms}
              partnerMap={partnerMap}
              onPartnerFeedPlatformsChange={onPartnerFeedPlatformsChange}
              onPlatformsChange={onPlatformsChange}
            />
          )}
          {customAvailable.length > 0 && partnerFeeds.length < 2 && (
            <PlatformCheckboxes title="Custom / Distribution Platforms" titleColor="warning.dark" platforms={customAvailable} selectedPlatforms={selectedPlatforms} onToggle={togglePlatform} />
          )}

          {/* Selected platforms as removable chips */}
          {selectedPlatforms.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
              {selectedPlatforms.map((p) => (
                <Chip
                  key={p}
                  label={p}
                  size="small"
                  color="primary"
                  onDelete={() => removePlatform(p)}
                  deleteIcon={<CloseIcon aria-label={`Remove ${p} from ${brand.name}`} />}
                  sx={{ minHeight: 32, "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.dark" } }}
                />
              ))}
            </Box>
          )}
        </Box>

        {selectedPlatforms.length === 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Select at least one platform for {brand.name}.
          </Alert>
        )}

        {selectedPlatforms.length > 0 && videos.length > 0 && (
          <>
            <Divider sx={{ mb: 1.5 }} />
            {videos.map((video, idx) => (
              <VideoSection
                key={video.unique_key}
                index={idx + 1}
                video={video}
                selectedPlatforms={selectedPlatforms}
                videoConfigs={brandConfigs[video.unique_key] || {}}
                getTemplate={getTemplate}
                getTemplateChoices={getTemplateChoices}
                onConfigChange={(platform, partialConfig) =>
                  onConfigChange(brand.name, video.unique_key, platform, partialConfig)
                }
              />
            ))}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default function Step3PlatformSource({
  selectedBrands,
  brandVideos,
  brandPlatforms,
  platformConfigs,
  selectedFeeds,
  brandFeedsInfo,
  onPlatformsChange,
  onConfigChange,
  onRemoveVideo,
  partnerFeedPlatforms = {},
  onPartnerFeedPlatformsChange,
}) {
  const totalBrandsWithoutPlatforms = selectedBrands.filter(
    (b) => (brandPlatforms[b.name] || []).length === 0
  ).length;

  return (
    <Box>
      <Typography variant="h6" component="h2" fontWeight={600} gutterBottom>
        Step 3 — Platforms & Sources
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Each brand shows only the platforms relevant to its selected feeds. Configure the video
        source for every combination.
      </Typography>

      {totalBrandsWithoutPlatforms > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {totalBrandsWithoutPlatforms} brand{totalBrandsWithoutPlatforms !== 1 ? "s" : ""} still need
          at least one platform selected.
        </Alert>
      )}

      <BatchEditToolbar
        selectedBrands={selectedBrands}
        brandVideos={brandVideos}
        brandPlatforms={brandPlatforms}
        platformConfigs={platformConfigs}
        onConfigChange={onConfigChange}
        onRemoveVideo={onRemoveVideo}
      />

      {selectedBrands.map((brand) => (
        <BrandSection
          key={brand.name}
          brand={brand}
          videos={brandVideos[brand.name] || []}
          selectedPlatforms={brandPlatforms[brand.name] || []}
          availablePlatforms={getAvailablePlatforms(brand.name, selectedFeeds, brandFeedsInfo)}
          onPlatformsChange={(platforms) => onPlatformsChange(brand.name, platforms)}
          brandConfigs={platformConfigs[brand.name] || {}}
          onConfigChange={onConfigChange}
          partnerFeeds={selectedPartnerFeeds(brand.name, selectedFeeds, brandFeedsInfo)}
          partnerMap={partnerFeedPlatforms[brand.name]}
          onPartnerFeedPlatformsChange={(map) => onPartnerFeedPlatformsChange?.(brand.name, map)}
        />
      ))}
    </Box>
  );
}
